import { ReactNode } from "react";
import clsx from "clsx";

export enum ButtonVariants {
  Primary = "primary",
  Outlined = "outlined",
}

export enum ButtonSizes {
  Small = "sm",
  Large = "lg",
}

type ButtonProps = {
  onClick?: () => void;
  children: ReactNode;
  variant?: ButtonVariants;
  size?: ButtonSizes;
  disabled?: boolean;
  className?: string;
  icon?: ReactNode;
  type?: "button" | "submit" | "reset";
};

const baseClasses =
  "flex items-center justify-center gap-1 w-full font-bold rounded-[100px]";

const variants: Record<
  ButtonVariants,
  {
    enabled: string;
    disabled: string;
  }
> = {
  [ButtonVariants.Primary]: {
    enabled:
      "border-none bg-gradient-to-r from-button_bg_secondary to-button_bg_primary text-button_text_primary",
    disabled:
      "border-none bg-button_bg_primary_disabled text-button_text_primary_disabled/70",
  },
  [ButtonVariants.Outlined]: {
    enabled:
      "bg-transparent border-2 border-button_bg_primary text-button_bg_primary",
    disabled:
      "bg-transparent border-2 border-button_bg_primary_disabled text-button_primary_disabled",
  },
};

const sizes: Record<ButtonSizes, string> = {
  [ButtonSizes.Small]: "text-xs py-2",
  [ButtonSizes.Large]: "text-xl py-[18px] leading-5",
};

export const Button = ({
  onClick,
  children,
  variant = ButtonVariants.Primary,
  size = ButtonSizes.Small,
  disabled = false,
  className = "",
  type = "button",
  icon,
}: ButtonProps) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        baseClasses,
        variants[variant][disabled ? "disabled" : "enabled"],
        sizes[size],
        disabled && "cursor-not-allowed",
        className,
      )}
    >
      {children}
      {icon && <span>{icon}</span>}
    </button>
  );
};
